import {
  getDateContext,
  getDisplayStatus,
  getDisplayStatusLabel,
  getStatusColor,
} from '@/utils/status';
import { useT } from '@/i18n';
import type { Reservation } from '@restaurant/api-client';

interface ReservationBadgeProps {
  status: Reservation['status'];
  arrivalStatus: Reservation['arrivalStatus'];
  /** UTC ISO timestamp from reservation.time - used to derive date context in restaurant local timezone. */
  reservationTime: string;
  onClick?: (e: React.MouseEvent) => void;
}

/** `#rrggbb` → `[r, g, b]`. The status palette is all 6-digit hex (utils/status). */
function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function ReservationBadge({
  status,
  arrivalStatus,
  reservationTime,
  onClick,
}: ReservationBadgeProps) {
  const t = useT();
  const dateCtx = getDateContext(reservationTime);
  const displayStatus = getDisplayStatus(status, arrivalStatus);
  const label = getDisplayStatusLabel(displayStatus, dateCtx, t.reservations.displayStatus);
  const color = getStatusColor(displayStatus);
  // Solid fill so the status reads clearly against the light page/card surface;
  // a soft tint at this size washes out (gray statuses especially). A thin ink
  // shadow lifts the pill off the surface so it never blends with the bg.
  const [r, g, b] = hexToRgb(color);
  // Staff can now flip from any status (no transition guards), so the badge
  // is always tappable and always shows a chevron-down hint.
  const tappable = !!onClick;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 11,
        padding: tappable ? '8px 10px 8px 12px' : '8px 12px',
        borderRadius: 10,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        height: 33,
        boxSizing: 'border-box',
        display: 'inline-flex',
        width: 'fit-content',
        alignItems: 'center',
        gap: tappable ? 4 : 0,
        background: color,
        color: 'white',
        border: 'none',
        boxShadow: `0 1px 2px rgba(${r}, ${g}, ${b}, 0.35)`,
        cursor: tappable ? 'pointer' : 'default',
      }}
    >
      {label}
      {tappable && (
        <i className="f7-icons" style={{ fontSize: 12, lineHeight: 1, width: 12, height: 12 }}>
          chevron_down
        </i>
      )}
    </button>
  );
}

export default ReservationBadge;
